import { Table } from "@mantine/core";
import dayjs from "dayjs";
import { useCallback } from "react";
import { useParams } from "react-router-dom";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import Loader from "src/components/shared/Loader/Loader";
import { useGetApi } from "src/hooks";
import { type ILink } from "src/interfaces";
import { PageContainer, StyledTd, StyledTh, StyledTr } from "src/styled";
import { getYachtId, titlecaseSentence } from "src/utils";

import { type IYachtSetupHistoryItem } from "../YachtSetup.types";
import { LayoutContainer } from "./YachtSetupHistory.styled";

const sections = {
  particulars: "particulars",
  gmdss: "gmdss",
  biofoul: "biofouling",
  tank: "tankplans",
  dynpos: "dps",
  cabins: "cabins",
  ozone: "ozone",
  med: "medlog",
};

const mapHistoryItems = (data: any) => data.list as IYachtSetupHistoryItem[];

const YachtSetupHistory = () => {
  const { section } = useParams();

  const { data, isLoading } = useGetApi({
    request: "get-yacht-hist",
    body: { params: { section, yachtid: getYachtId() } },
    mapper: mapHistoryItems,
  });

  const getLinks = useCallback(
    (links: ILink[]) => {
      const prevSection = sections[section as keyof typeof sections];
      if (prevSection) {
        let title = titlecaseSentence(prevSection.replaceAll("-", " "));
        title = title.replace("Gmdss", "GMDSS");
        title = title.replace("Nmea", "NMEA");
        links.splice(links.length - 1, 0, {
          title,
          href: `/settings/yacht/${prevSection}`,
        });
      }
      return links;
    },
    [section],
  );

  const renderUser = useCallback((role: string, name: string) => {
    let text = "";
    if (role) text += role;
    if (name) text += ` ${name}`;
    return text;
  }, []);

  const renderDiff = useCallback((diff: string | string[]) => {
    const defaultDiff = "Details Changed";

    if (!diff) {
      return defaultDiff;
    }

    if (typeof diff === "string") {
      return diff ?? defaultDiff;
    } else if (Array.isArray(diff)) {
      if (diff.length === 0) {
        return defaultDiff;
      }
      return diff.map(d => <p key={d}>{d}</p>);
    }

    return defaultDiff;
  }, []);

  return (
    <PageContainer>
      <Breadcrumbs customLinks={getLinks} />
      <LayoutContainer>
        <Table>
          <thead>
            <StyledTr>
              <StyledTh>Edit description</StyledTh>
              <StyledTh>Edited by</StyledTh>
              <StyledTh>Edit date</StyledTh>
            </StyledTr>
          </thead>
          <tbody>
            {data &&
              !isLoading &&
              data.map(row => (
                <StyledTr key={row.hid}>
                  <StyledTd>{renderDiff(row.diff)}</StyledTd>
                  <StyledTd>{renderUser(row.rolename, row.full_name)}</StyledTd>
                  <StyledTd>
                    {row.timestamp
                      ? dayjs(row.timestamp).format("D MMM YYYY - HH:mm")
                      : row.timestamp}
                  </StyledTd>
                </StyledTr>
              ))}
          </tbody>
        </Table>
      </LayoutContainer>
      {!(data ?? isLoading) && <Loader />}
    </PageContainer>
  );
};

export default YachtSetupHistory;
